import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft, ArrowUpRight, MoreHorizontal, PauseCircle, PlayCircle, Trash2,
  LayoutDashboard, Building, PlugZap, Palette, UserCog, Receipt, FileText, History, Send,
} from 'lucide-react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nProvider';
import { useSession } from '../../../session/SessionProvider';
import { errorText } from '../../../lib/errors';
import { Button, Tabs, StatusBadge, Avatar, Dropdown, DropdownItem, DropdownDivider, ConfirmDialog, LoadingBlock, EmptyState } from '../../../ui';
import OverviewTab from './OverviewTab';
import ProfileTab from './ProfileTab';
import PosTab from './PosTab';
import BillingTab from './BillingTab';
import DocumentsTab from './DocumentsTab';
import TelegramBotTab from '../../company/TelegramBotTab';
import BrandingEditor from '../../../features/BrandingEditor';
import { TeamPanel } from '../../company/TeamPage';
import { AuditPanel } from '../../shared/AuditPage';

export default function CompanyDetailPage() {
  const { id } = useParams();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { enterCompany } = useSession();
  const [params, setParams] = useSearchParams();
  const [tenant, setTenant] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'suspend' | 'delete'
  const [busy, setBusy] = useState(false);

  const tab = params.get('tab') || 'overview';
  const setTab = (next) => setParams(next === 'overview' ? {} : { tab: next }, { replace: true });

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/tenants/${id}`);
      setTenant(res.data.data);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else toast.error(errorText(err, t('common.error')));
    }
  }, [id, t]);

  useEffect(() => { setTenant(null); load(); }, [load]);

  if (notFound) return <EmptyState icon={Building} title={t('companies.empty')} action={<Link to="/owner/companies"><Button variant="secondary">{t('company.back')}</Button></Link>} />;
  if (!tenant) return <LoadingBlock />;

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.put(`/admin/tenants/${id}`, { status });
      toast.success(t('company.statusChanged'));
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/tenants/${id}`);
      toast.success(t('company.deleted'));
      navigate('/owner/companies');
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const openWorkspace = () => {
    enterCompany(tenant);
    navigate('/company');
  };

  const tabs = [
    { id: 'overview', label: t('company.tabs.overview'), icon: LayoutDashboard },
    { id: 'profile', label: t('company.tabs.profile'), icon: Building },
    { id: 'pos', label: t('company.tabs.pos'), icon: PlugZap },
    { id: 'branding', label: t('company.tabs.branding'), icon: Palette },
    { id: 'team', label: t('company.tabs.team'), icon: UserCog },
    { id: 'billing', label: t('company.tabs.billing'), icon: Receipt },
    { id: 'documents', label: t('company.tabs.documents'), icon: FileText },
    { id: 'telegram', label: t('company.tabs.telegram'), icon: Send },
    { id: 'activity', label: t('company.tabs.activity'), icon: History },
  ];

  return (
    <>
      <Link to="/owner/companies" className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100">
        <ArrowLeft className="h-4 w-4" />
        {t('company.back')}
      </Link>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <Avatar name={tenant.name} size="lg" className="rounded-xl" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50 sm:text-2xl">{tenant.name}</h1>
              <StatusBadge kind="company" value={tenant.status} />
            </div>
            <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
              <span className="font-mono">{tenant.company_code}</span>
              {tenant.legal_name ? ` · ${tenant.legal_name}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={ArrowUpRight} onClick={openWorkspace}>{t('company.openWorkspace')}</Button>
          <Dropdown
            trigger={(open, toggle) => (
              <Button variant="secondary" size="icon" onClick={toggle} aria-label={t('common.actions')} aria-expanded={open}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            )}
          >
            {tenant.status === 'suspended' ? (
              <DropdownItem icon={PlayCircle} onClick={() => setStatus('active')}>{t('company.activate')}</DropdownItem>
            ) : (
              <DropdownItem icon={PauseCircle} onClick={() => setConfirm('suspend')}>{t('company.suspend')}</DropdownItem>
            )}
            {!tenant.is_default && (
              <>
                <DropdownDivider />
                <DropdownItem icon={Trash2} tone="danger" onClick={() => setConfirm('delete')}>{t('company.deleteCompany')}</DropdownItem>
              </>
            )}
          </Dropdown>
        </div>
      </div>

      <Tabs items={tabs} value={tab} onChange={setTab} className="mb-6" />

      {tab === 'overview' && <OverviewTab tenant={tenant} onOpenTab={setTab} />}
      {tab === 'profile' && <ProfileTab tenant={tenant} onSaved={load} />}
      {tab === 'pos' && <PosTab tenant={tenant} onSaved={load} />}
      {tab === 'branding' && <BrandingEditor tenantId={tenant.id} companyName={tenant.name} />}
      {tab === 'team' && <TeamPanel tenantId={tenant.id} embedded />}
      {tab === 'billing' && <BillingTab tenant={tenant} onSaved={load} />}
      {tab === 'documents' && <DocumentsTab tenant={tenant} />}
      {tab === 'telegram' && <TelegramBotTab tenant={tenant} />}
      {tab === 'activity' && <AuditPanel tenantId={tenant.id} embedded />}

      <ConfirmDialog
        open={confirm === 'suspend'}
        onClose={() => setConfirm(null)}
        onConfirm={() => setStatus('suspended')}
        loading={busy}
        title={t('company.suspendTitle')}
        text={t('company.suspendText', { name: tenant.name })}
        confirmLabel={t('company.suspend')}
      />
      <ConfirmDialog
        open={confirm === 'delete'}
        onClose={() => setConfirm(null)}
        onConfirm={remove}
        loading={busy}
        title={t('company.deleteTitle')}
        text={t('company.deleteText', { name: tenant.name })}
        confirmLabel={t('common.delete')}
      />
    </>
  );
}
